// GA4, loaded from here instead of public/index.html (Signup/docs/35 S1). It loads only when all
// three hold: the build has an id (.env.production only), the build says it is production
// (REACT_APP_ENVIRONMENT), and the page is on castingworkbook.com, so a production build run on
// localhost or a preview host stays quiet too. Otherwise nothing is loaded and nothing leaves the
// browser; events still land in window.dataLayer, where the tests read them.
//
// Page views are sent by the app (GaPageViews), with the URL cleaned of session keys and
// one-time codes, so config turns GA's own first page view off. gtag.js would otherwise put the
// raw address (k included) on every hit, so every hit carries the cleaned one instead.

export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";
export const BUILD_ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || "";

export const isProductionHost = (hostname: string): boolean => {
  const host = String(hostname || "").toLowerCase();
  return host === "castingworkbook.com" || host.endsWith(".castingworkbook.com");
};

/** GA4 runs only for a production build with an id, on the production site. */
export const gaEnabled = (measurementId: string, environment: string, hostname: string): boolean =>
  !!measurementId && environment === "production" && isProductionHost(hostname);

// The only query parameters GA4 sees: campaign tags, ad click ids, and the flow's public context.
// Everything else (keys, codes, emails, client or submission ids) stays out. Keep IDENTICAL to
// React-Membership's helpers/ga.js.
export const PUBLIC_PARAMS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id",
  "gclid", "gbraid", "wbraid", "fbclid", "msclkid", "ttclid",
  "src", "flow", "bd", "roleid", "step4", "ctry", "lang", "page", "liteOffer",
];

// Never sent to Sentry either: sign-in keys, one-time codes, the signed email reference.
export const SECRET_PARAMS = ["k", "tk", "sessionId", "code", "join", "promo", "eref", "t"];

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

let started = false;

export const initGa = (
  measurementId: string = GA_MEASUREMENT_ID,
  environment: string = BUILD_ENVIRONMENT,
  hostname: string = window.location.hostname
) => {
  if (started) return;
  started = true;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    // gtag.js's own stub: it reads the arguments object as pushed.
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
  if (!gaEnabled(measurementId, environment, hostname)) return;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
  window.gtag("js", new Date());
  window.gtag("config", measurementId, { send_page_view: false, ...pageFields() });
};

// The address with its query filtered. A relative one ("/path?k=…", as Sentry's breadcrumbs have
// it) stays relative. One that can't be read loses its whole query.
const filterQuery = (href: string, keep: (name: string) => boolean): string => {
  if (!href) return href;
  try {
    const absolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(href);
    const url = new URL(href, "https://relative.invalid");
    Array.from(url.searchParams.keys())
      .filter((name) => !keep(name))
      .forEach((name) => url.searchParams.delete(name));
    return absolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
  } catch (e) {
    return String(href).split("?")[0];
  }
};

/** The address as GA4 may see it: only the public parameters. */
export const cleanUrl = (href: string): string => filterQuery(href, (name) => PUBLIC_PARAMS.indexOf(name) >= 0);

/** The address without the secret parameters, everything else kept (Sentry, for debugging). */
export const withoutSecrets = (href: string): string => filterQuery(href, (name) => SECRET_PARAMS.indexOf(name) < 0);

// The page fields gtag.js would fill from the browser, cleaned.
const pageFields = () => ({
  page_location: cleanUrl(window.location.href),
  page_referrer: cleanUrl(document.referrer) || undefined,
});

export const gaEvent = (name: string, params: Record<string, any> = {}) => {
  try {
    if (typeof window === "undefined" || !window.gtag) return;
    const page = pageFields();
    // For the hits gtag.js sends by itself (enhanced measurement), then for this one.
    window.gtag("set", page);
    window.gtag("event", name, { ...page, ...params });
  } catch (e) {
    // Analytics never breaks a page.
  }
};

const SIGN_UP_SENT_KEY = "gaSignUpSent.v1";

/**
 * GA4's sign_up when the classic wizard creates an account (Signup/docs/41 U11), once per account
 * in this tab: the wizard can create it from two steps. The submission flow sends its own
 * sf_account_created; GA4 cross-checks the sign-ups of both paths against SQL.
 */
export const trackClassicSignUp = (clientId: number | string | null | undefined) => {
  if (!clientId) return;
  const key = String(clientId);
  try {
    const raw = window.sessionStorage.getItem(SIGN_UP_SENT_KEY);
    const sent: string[] = raw ? JSON.parse(raw) : [];
    if (sent.indexOf(key) >= 0) return;
    window.sessionStorage.setItem(SIGN_UP_SENT_KEY, JSON.stringify(sent.concat(key).slice(-20)));
  } catch (e) {
    // Without storage the event may come twice for one account; never not at all.
  }
  gaEvent("sign_up", { method: "classic" });
};

export const sendPageView = (href: string = window.location.href, title: string = document.title) =>
  gaEvent("page_view", { page_location: cleanUrl(href), page_title: title });
